import { useQuery, useQueryClient } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import { useRef, useState } from "react";

import { ProfileName } from "@/components/profiles/name";
import { ProfilePhoto } from "@/components/profiles/photo";
import { levelsQueryOptions } from "@/data/levels";
import type { PlayerProfile } from "@/db/schema";
import type { Gender } from "@/db/schema/shared";
import { useTRPC } from "@/integrations/trpc/react";
import { isDefined } from "@/utils/types";

import { Button } from "../../button";
import { AsyncComboBoxField } from "./combo-box";
import { Description, type FieldProps } from "./shared";

export type ProfilePickerFieldProps = FieldProps & {
  isDisabled?: boolean;
  gender?: Gender;
  qualifiesDivision?: number;
  selectedProfileIds: number[];
};

export function ProfilePickerField({
  label,
  description,
  field,
  isDisabled,
  gender,
  qualifiesDivision,
  selectedProfileIds,
}: ProfilePickerFieldProps) {
  const trpc = useTRPC();
  const profiles = useRef<PlayerProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const previousValue = useRef<number | null>(null);

  const queryClient = useQueryClient();

  const { data: levelsForDivision } = useQuery({
    ...levelsQueryOptions({ division: qualifiesDivision }),
    enabled: isDefined(qualifiesDivision),
    select: (data) => data.map(({ id }) => id),
  });

  const { data: selectedProfile } = useQuery({
    ...trpc.profiles.listByIds.queryOptions({
      ids: selectedProfileIds ?? [],
    }),
    enabled: (selectedProfileIds?.length ?? 0) > 0,
    select: (data) => data.find(({ id }) => id === field.state.value),
  });

  if (selectedProfile && !isSearching) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-between">
          <span className="flex flex-row space-x-2">
            <ProfilePhoto {...selectedProfile} />
            <ProfileName {...selectedProfile} />
          </span>
          <Button
            className="text-red-500"
            variant="icon"
            onPress={() => {
              previousValue.current = field.state.value;
              setIsSearching(true);
              field.handleChange(null);
            }}
            isDisabled={isDisabled}
          >
            <XIcon size={16} />
          </Button>
        </div>
        {description && (
          <Description className="text-amber-700">{description}</Description>
        )}
      </div>
    );
  }

  const cancelSearch = () => {
    if (previousValue.current != null) {
      field.handleChange(previousValue.current);
      setIsSearching(false);
      previousValue.current = null;
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keydown handler for Escape cancel
    <div
      className="flex flex-col items-start"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          cancelSearch();
        }
      }}
    >
      <AsyncComboBoxField
        className="w-full"
        label={label}
        field={field}
        isDisabled={isDisabled}
        placeholder="Start typing a name or username..."
        onSelectionChange={(next) => {
          const profile = profiles.current?.find(({ id }) => id === next);
          field.handleChange(profile?.id ?? null);
          if (profile) {
            setIsSearching(false);
          }
        }}
        fetchOptions={{
          load: async ({ filterText }) => {
            if (!filterText || filterText.length < 3) {
              return { items: [] };
            }

            const result = await queryClient.ensureQueryData(
              trpc.profiles.list.queryOptions({
                query: filterText,
                levels: levelsForDivision,
                gender: gender && gender !== "coed" ? gender : undefined,
              }),
            );

            profiles.current = result;

            return {
              items: result.map(
                ({ id, preferredName, firstName, lastName, ...profile }) => ({
                  beforeDisplay: (
                    <ProfilePhoto
                      preferredName={preferredName}
                      firstName={firstName}
                      lastName={lastName}
                      {...profile}
                    />
                  ),
                  display: `${preferredName || firstName} ${lastName}`,
                  value: id,
                }),
              ),
            };
          },
        }}
      />
      {previousValue.current != null && (
        <Button variant="text" size="sm" onPress={cancelSearch}>
          Cancel
        </Button>
      )}
    </div>
  );
}
